import { ConnectButton } from '@rainbow-me/rainbowkit'
import { Power, PowerOff, RefreshCw } from 'lucide-react'
import { useDisconnect } from 'wagmi'

import { useTranslation } from '@/features/locale/LocaleProvider'
import type { Messages } from '@/features/locale/messages'
import { cn } from '@/lib/utils'
import { isAddress, shortenAddress } from '@/utils'

import {
  WalletMarkIcon,
  walletMarkBaySize,
  walletStatusZoneHover,
  walletStatusZoneTone,
  type WalletMarkState,
} from './WalletMarkIcon'

export type WalletConnectVariant = 'header' | 'drawer' | 'cta'

export interface WalletConnectControlProps {
  className?: string
  variant?: WalletConnectVariant
}

const SESSION_BLOCK_CLASS = 'h-10'
const SESSION_ACTION_CLASS = 'size-10'
const SESSION_ACTION_ICON_CLASS = 'size-4'

type SessionVariant = Exclude<WalletConnectVariant, 'cta'>

type WalletLabels = Messages['wallet']

type SessionAccount = {
  address: string
  displayName: string
}

type CustomRenderProps = {
  account?: SessionAccount
  chain?: {
    id: number
    name?: string
    unsupported?: boolean
  }
}

function resolveMarkState(account: CustomRenderProps['account'], chain: CustomRenderProps['chain']): WalletMarkState {
  if (!account) return 'disconnected'
  if (chain?.unsupported) return 'wrong-network'
  return 'connected'
}

function statusLabel(state: WalletMarkState, wallet: WalletLabels): string {
  if (state === 'connected') return wallet.status.online
  if (state === 'wrong-network') return wallet.status.wrongNetwork
  return wallet.status.offline
}

function statusZoneAriaLabel(
  state: WalletMarkState,
  wallet: WalletLabels,
  account?: SessionAccount,
): string {
  const label = statusLabel(state, wallet)

  if (state === 'disconnected') {
    return `${label}. ${wallet.openConnect}.`
  }

  if (state === 'wrong-network') {
    const id = account ? `${account.displayName}. ` : ''
    return `${label}. ${id}${wallet.switchNetworkAction}.`
  }

  if (account) {
    return `${label}. ${account.displayName}. ${wallet.openAccount}.`
  }

  return label
}

function shellTone(state: WalletMarkState): string {
  if (state === 'wrong-network') {
    return 'border-destructive/40'
  }
  if (state === 'connected') {
    return 'border-primary/25'
  }
  return 'border-border/80'
}

/** Etiqueta compacta y predecible para la wallet (evita doble elipsis de RainbowKit + CSS). */
function formatWalletLabel(account: SessionAccount, variant: SessionVariant): string {
  if (!isAddress(account.address) && !account.displayName.startsWith('0x')) {
    return account.displayName
  }

  const parsed = isAddress(account.address)
  if (!parsed) {
    return account.displayName
  }

  if (variant === 'drawer') {
    return `${parsed.slice(0, 6)}…${parsed.slice(-4)}`
  }

  return shortenAddress(parsed, 4)
}

function walletLabelTitle(account: SessionAccount): string {
  const parsed = isAddress(account.address)
  return parsed || account.displayName
}

function WalletConnectSkeleton({ variant, className }: { variant: WalletConnectVariant; className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-lg bg-muted/50',
        variant === 'cta' ? 'mx-auto h-12 w-full max-w-xs' : 'h-10 w-full max-w-full',
        className,
      )}
      aria-hidden
    />
  )
}

function WalletConnectCta({ onConnect, className }: { onConnect: () => void; className?: string }) {
  const t = useTranslation()

  return (
    <button
      type="button"
      onClick={onConnect}
      aria-label={t.wallet.connect}
      className={cn(
        'inline-flex w-full items-center justify-center rounded-xl border border-transparent bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90',
        className,
      )}
    >
      {t.wallet.connect}
    </button>
  )
}

function WalletSessionActionButton({
  state,
  variant,
  onConnect,
  onDisconnect,
  onSwitchNetwork,
}: {
  state: WalletMarkState
  variant: SessionVariant
  onConnect: () => void
  onDisconnect: () => void
  onSwitchNetwork: () => void
}) {
  const t = useTranslation()

  if (state === 'disconnected') {
    return (
      <button
        type="button"
        onClick={onConnect}
        aria-label={t.wallet.connectAction}
        className={cn(
          'inline-flex shrink-0 cursor-pointer items-center justify-center self-stretch bg-card text-primary transition-colors hover:bg-primary/10',
          SESSION_ACTION_CLASS,
        )}
      >
        <Power className={SESSION_ACTION_ICON_CLASS} aria-hidden />
      </button>
    )
  }

  if (state === 'connected') {
    return (
      <button
        type="button"
        onClick={onDisconnect}
        aria-label={t.wallet.disconnectAction}
        className={cn(
          'inline-flex shrink-0 cursor-pointer items-center justify-center self-stretch bg-card text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground',
          SESSION_ACTION_CLASS,
        )}
      >
        <PowerOff className={SESSION_ACTION_ICON_CLASS} aria-hidden />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onSwitchNetwork}
      aria-label={t.wallet.switchNetworkAction}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center self-stretch bg-card text-destructive transition-colors hover:bg-destructive/10',
        SESSION_ACTION_CLASS,
      )}
    >
      <RefreshCw className={SESSION_ACTION_ICON_CLASS} aria-hidden />
    </button>
  )
}

function WalletSessionStatusButton({
  state,
  variant,
  account,
  onOpenInfo,
}: {
  state: WalletMarkState
  variant: SessionVariant
  account?: SessionAccount
  onOpenInfo: () => void
}) {
  const t = useTranslation()
  const isHeader = variant === 'header'
  const showAccountId = account && state !== 'disconnected'
  const walletLabel = showAccountId ? formatWalletLabel(account, variant) : null
  const isEnsLabel = walletLabel != null && !walletLabel.startsWith('0x')

  return (
    <button
      type="button"
      onClick={onOpenInfo}
      aria-label={statusZoneAriaLabel(state, t.wallet, account)}
      className={cn(
        'flex min-w-0 cursor-pointer items-stretch text-left transition-colors',
        walletStatusZoneTone(state),
        walletStatusZoneHover(state),
        isHeader ? 'shrink-0' : 'min-w-0 flex-1',
      )}
    >
      <div className={cn('grid shrink-0 place-items-center overflow-hidden', walletMarkBaySize)}>
        <div className="flex size-[72%] items-center justify-center">
          <WalletMarkIcon state={state} />
        </div>
      </div>

      <div
        className={cn(
          'flex min-w-0 flex-1 items-center',
          isHeader ? 'gap-1.5 pr-2.5' : 'gap-2 pr-3',
        )}
      >
        <span
          className={cn(
            'shrink-0 whitespace-nowrap font-medium leading-none',
            'text-sm',
            state === 'wrong-network'
              ? 'text-destructive'
              : state === 'connected'
                ? 'text-foreground'
                : 'text-muted-foreground',
          )}
        >
          {statusLabel(state, t.wallet)}
        </span>

        {walletLabel ? (
          <>
            <span aria-hidden className="shrink-0 text-muted-foreground/45">
              ·
            </span>
            <span
              className={cn(
                'font-mono text-xs text-muted-foreground',
                isEnsLabel ? 'min-w-0 truncate' : 'shrink-0 tabular-nums',
              )}
              title={account ? walletLabelTitle(account) : undefined}
            >
              {walletLabel}
            </span>
          </>
        ) : null}
      </div>
    </button>
  )
}

function WalletSessionBlock({
  state,
  variant,
  account,
  onConnect,
  onOpenInfo,
  onSwitchNetwork,
  className,
}: {
  state: WalletMarkState
  variant: SessionVariant
  account?: SessionAccount
  onConnect: () => void
  onOpenInfo: () => void
  onSwitchNetwork: () => void
  className?: string
}) {
  const { disconnect } = useDisconnect()
  const isHeader = variant === 'header'

  return (
    <div
      className={cn(
        'flex items-stretch overflow-hidden rounded-lg border bg-card',
        shellTone(state),
        SESSION_BLOCK_CLASS,
        isHeader ? 'inline-flex max-w-full shrink-0' : 'w-full',
        className,
      )}
    >
      <WalletSessionStatusButton
        state={state}
        variant={variant}
        account={account}
        onOpenInfo={onOpenInfo}
      />

      <div className="w-px shrink-0 self-stretch bg-border/80" aria-hidden />

      <WalletSessionActionButton
        state={state}
        variant={variant}
        onConnect={onConnect}
        onDisconnect={() => disconnect()}
        onSwitchNetwork={onSwitchNetwork}
      />
    </div>
  )
}

function resolveOpenInfoHandler(
  state: WalletMarkState,
  openConnectModal: () => void,
  openAccountModal: () => void,
  openChainModal: () => void,
): () => void {
  if (state === 'disconnected') return openConnectModal
  if (state === 'wrong-network') return openChainModal
  return openAccountModal
}

/** Conector wallet con UI de producto (RainbowKit modals + marca Don't Panic). */
export function WalletConnectControl({ className, variant = 'header' }: WalletConnectControlProps) {
  return (
    <ConnectButton.Custom>
      {({ account, chain, openAccountModal, openChainModal, openConnectModal, mounted }) => {
        if (!mounted) {
          return <WalletConnectSkeleton variant={variant} className={className} />
        }

        if (variant === 'cta') {
          if (resolveMarkState(account, chain) !== 'disconnected') {
            return null
          }
          return <WalletConnectCta onConnect={openConnectModal} className={className} />
        }

        const markState = resolveMarkState(account, chain)

        return (
          <WalletSessionBlock
            state={markState}
            variant={variant}
            account={account}
            onConnect={openConnectModal}
            onOpenInfo={resolveOpenInfoHandler(
              markState,
              openConnectModal,
              openAccountModal,
              openChainModal,
            )}
            onSwitchNetwork={openChainModal}
            className={className}
          />
        )
      }}
    </ConnectButton.Custom>
  )
}
